import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AuthContext } from '../context/AuthContext';
import { clearPhoneVerified, isPhoneVerified, markPhoneVerified } from '../services/phoneCert';

/**
 * 야옹콘 등 휴대폰 인증이 필요한 화면에서 사용.
 *
 * @param {'gate' | 'redirect'} mode
 * - gate: 미인증이면 리다이렉트하지 않고 needsCert=true (오버레이 게이트용)
 * - redirect: 미인증이면 /verification 으로 이동
 */
export default function useRequirePhoneCert({
  enabled = true,
  redirectTo,
  redirectState,
  mode = 'gate',
} = {}) {
  const { API_SERVER_DOMAIN, useAccessToken } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(enabled ? 'checking' : 'ready');
  const redirectStateRef = useRef(redirectState);
  redirectStateRef.current = redirectState;

  const targetPath = redirectTo || `${location.pathname}${location.search || ''}`;

  useEffect(() => {
    if (!enabled) {
      setStatus('ready');
      return;
    }
    if (!accessToken) {
      setStatus('checking');
      return;
    }

    let cancelled = false;

    (async () => {
      setStatus('checking');
      const verified = await isPhoneVerified(API_SERVER_DOMAIN, accessToken);
      if (cancelled) return;

      if (verified) {
        markPhoneVerified();
        setStatus('ready');
        return;
      }

      clearPhoneVerified();

      if (mode === 'redirect') {
        setStatus('redirecting');
        navigate('/verification', {
          replace: true,
          state: {
            accessToken,
            redirectTo: targetPath,
            redirectState:
              redirectStateRef.current !== undefined ? redirectStateRef.current : location.state,
          },
        });
        return;
      }

      setStatus('needsCert');
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.state는 ref로만 사용
  }, [enabled, accessToken, API_SERVER_DOMAIN, navigate, targetPath, mode]);

  const goToVerification = () => {
    navigate('/verification', {
      state: {
        accessToken,
        redirectTo: targetPath,
        redirectState:
          redirectStateRef.current !== undefined ? redirectStateRef.current : location.state,
      },
    });
  };

  return {
    ready: status === 'ready',
    verified: status === 'ready',
    needsCert: status === 'needsCert',
    checking: status === 'checking' || status === 'redirecting',
    goToVerification,
  };
}
